import { call, put, select, takeEvery } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import { AxiosError } from 'axios';

import { RootState } from '../reducers/rootReducer';
import { acknowledgePickTour } from '../actions/acknowledgePickTour.actions';
import { setLoading } from '../actions/loading.actions';
import { setAppPage } from '../actions/appPage.actions';
import { setPickTourWithError } from '../actions/orders.actions';
import { createToast } from '../actions/toasts.actions';

import {
  SAGA_ACKNOWLEDGE_PICK_TOUR,
  BFF_V1,
  BFF_PICK_TOURS,
  BFF_ACKNOWLEDGE,
  PICKING_APIGEE_BASE_PATH,
  APP_PAGE_ORDER_LIST,
} from '../../constants';

import { ConfigAuthValues } from '../../types';
import { callAPI, createApiResponseTimeHelper } from '../../utils';
import { handleApiErrors, catchApiErrors } from './utils';

const callAcknowledgePickTour = (pickTourID: string, configAuthValues: ConfigAuthValues) => {
  return callAPI({
    method: 'POST',
    url: `${configAuthValues.APIGEE_BASE_URL}${PICKING_APIGEE_BASE_PATH}${BFF_V1}${BFF_PICK_TOURS}/${pickTourID}${BFF_ACKNOWLEDGE}`,
    data: {},
  });
};

export function* runAcknowledgePickTourSaga(action: ReturnType<typeof acknowledgePickTour>): Generator<any, void, any> {
  yield put(setLoading({ sagaName: SAGA_ACKNOWLEDGE_PICK_TOUR, isLoading: true }));

  const configAuthValues: ConfigAuthValues | null = yield select((state: RootState) => state.config.authValues);
  if (!configAuthValues) {
    console.error('acknowledgePickTour saga: configAuthValues is null');
    yield put(
      createToast({
        type: 'error',
        message: 'Configuration not loaded. Please refresh the page.',
      })
    );
    yield put(setLoading({ sagaName: SAGA_ACKNOWLEDGE_PICK_TOUR, isLoading: false }));
    return;
  }

  const getApiResponseTime = createApiResponseTimeHelper();

  try {
    const { data } = yield call(callAcknowledgePickTour, action.payload.pickTourID, configAuthValues);
    const apiResponseTime = getApiResponseTime();
    // Handle any errors from the response
    if (data.errors && data.errors.length > 0) {
      yield handleApiErrors(
        data.errors,
        {
          saga: SAGA_ACKNOWLEDGE_PICK_TOUR,
          method: 'POST',
          url: `${PICKING_APIGEE_BASE_PATH}${BFF_V1}${BFF_PICK_TOURS}/${action.payload.pickTourID}${BFF_ACKNOWLEDGE}`,
          data: {},
          config: configAuthValues,
        },
        apiResponseTime
      );
    } else {
      // Success - clear error pick tour and return to order list
      yield put(setPickTourWithError({ errorPickTour: null }));
      yield put(setAppPage({ name: APP_PAGE_ORDER_LIST }));
    }
  } catch (err) {
    const apiResponseTime = getApiResponseTime();
    const error = err as AxiosError;
    yield catchApiErrors(
      error,
      {
        saga: SAGA_ACKNOWLEDGE_PICK_TOUR,
        method: 'POST',
        url: `${PICKING_APIGEE_BASE_PATH}${BFF_V1}${BFF_PICK_TOURS}/${action.payload.pickTourID}${BFF_ACKNOWLEDGE}`,
        data: {},
        config: configAuthValues,
      },
      apiResponseTime
    );
  } finally {
    yield put(setLoading({ sagaName: SAGA_ACKNOWLEDGE_PICK_TOUR, isLoading: false }));
  }
}

export default function* watchAcknowledgePickTour() {
  yield takeEvery(getType(acknowledgePickTour), runAcknowledgePickTourSaga);
}
